<template>
  <base-modal
    :show="open"
    :title="$t('Site.FieldOrder.ConfigureFieldsOrder')"
    :showCancelButton="true"
    maxWidth="90%" hideActions
    @on-cancel="hide()"
  >
    <v-divider></v-divider>

    <v-card-text class="pa-4">
      <v-row>
        <v-col cols="12" md="4">
          <v-card elevation="1" rounded="lg">
            <v-card-subtitle class="pb-2 font-weight-medium primary--text">
              {{ $t('ContentTypes') }}
            </v-card-subtitle>
            <v-divider></v-divider>
            <v-list dense class="py-0">
              <v-list-item-group v-model="selectedContentTypeIndex" color="primary">
                <v-hover v-for="(ct, index) in contentTypes" :key="ct.id" v-slot="{ hover }">
                  <v-list-item @click="handleContentTypeClick(index)" :class="{'elevation-1': hover}">
                    <v-list-item-avatar color="primary" size="32">
                      <v-icon small color="white">mdi-file-document</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">{{ ct.name }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-icon v-if="hasUnsavedChanges(ct.id)">
                      <v-tooltip left>
                        <template v-slot:activator="{ on }">
                          <v-icon color="warning" small v-on="on">mdi-circle</v-icon>
                        </template>
                        <span>{{ $t('Site.FieldOrder.UnsavedChangesWarning') }}</span>
                      </v-tooltip>
                    </v-list-item-icon>
                  </v-list-item>
                </v-hover>
              </v-list-item-group>
            </v-list>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <v-card elevation="1" rounded="lg" v-if="selectedContentType">
            <v-card-subtitle class="pb-2 d-flex align-center primary--text font-weight-medium">
              <v-icon left color="primary">mdi-order-bool-ascending</v-icon>
              {{ $t('Site.FieldOrder.FieldsOrder') }} - {{ selectedContentType.name }}
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                @click="saveCurrentContentType"
                :disabled="!hasUnsavedChanges(selectedContentType.originalContentTypeID)"
                :loading="loading"
                rounded
                elevation="0"
              >
                <v-icon left>mdi-content-save</v-icon>
                {{ $t('Save') }}
              </v-btn>
            </v-card-subtitle>
            <v-divider></v-divider>

            <v-card-text class="pa-4" style="max-height: 500px; overflow-y: auto;">
              <v-alert
                v-if="hasUnsavedChanges(selectedContentType.originalContentTypeID)"
                dense
                text
                type="warning"
                class="mb-4"
                prominent
                border="left"
              >
                <v-icon slot="prepend" color="warning">mdi-alert</v-icon>
                {{ $t('Site.FieldOrder.UnsavedChangesWarning') }}
              </v-alert>

              <draggable
                v-model="currentFields"
                handle=".drag-handle"
                @start="drag = true"
                @end="drag = false"
                @change="onFieldOrderChange"
              >
                <transition-group type="transition" name="flip-list">
                  <v-hover v-for="(field, index) in currentFields" :key="field.code" v-slot="{ hover }">
                    <v-card
                      :elevation="hover ? 4 : 1"
                      rounded="lg"
                      class="drag-handle mb-3"
                    >
                      <v-card-text class="pa-3 d-flex align-center">
                        <v-icon class="mr-3" color="grey darken-1">
                          mdi-drag-horizontal-variant
                        </v-icon>

                        <v-avatar color="blue-grey lighten-4" size="40" class="mr-3">
                          <v-icon color="blue-grey darken-2">mdi-form-textbox</v-icon>
                        </v-avatar>

                        <div class="flex-grow-1">
                          <div class="font-weight-medium body-1">{{ field.title }}</div>
                          <div class="text-caption blue-grey--text text--darken-1">{{ field.code }}</div>
                        </div>

                        <v-chip
                          color="primary"
                          outlined
                          label
                        >
                          {{ index + 1 }}
                        </v-chip>
                      </v-card-text>
                    </v-card>
                  </v-hover>
                </transition-group>
              </draggable>

              <v-card v-if="!currentFields || currentFields.length === 0" flat class="text-center py-8">
                <v-icon size="64" color="grey lighten-1">mdi-tray-alert</v-icon>
                <div class="mt-3 grey--text body-1">
                  {{ $t('Site.FieldOrder.NoFieldsAvailable') }}
                </div>
              </v-card>
            </v-card-text>
          </v-card>

          <v-card v-else flat class="text-center py-10" elevation="0">
            <v-icon size="80" color="grey lighten-2">mdi-cursor-pointer</v-icon>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>

    <v-divider></v-divider>
  </base-modal>
</template>

<script>
import draggable from 'vuedraggable';
import UserContentTypeResultsOrdersService from '@/services/UserContentTypeResultsOrdersService';
import Utils from '@/ecm/common/Utils';

export default {
    name: 'FieldOrderConfigModal',
    components: {
        draggable,
    },
    props: {
        contentTypes: {
            type: Array,
            required: true,
        },
        existingConfigs: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            selectedContentTypeIndex: null,
            currentFields: [],
            unsavedChanges: {},
            originalFieldsOrder: {},
            drag: false,
            open: false,
            loading: false,
            madeChanges: false,
        };
    },
    computed: {
        selectedContentType() {
            if (this.selectedContentTypeIndex === null || this.selectedContentTypeIndex === undefined) {
                return null;
            }
            return this.contentTypes[this.selectedContentTypeIndex];
        },
    },
    watch: {
        contentTypes: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    newVal.forEach(ct => {
                        if (ct.fields && ct.fields.length > 0) {
                            this.originalFieldsOrder[ct.id] = JSON.parse(JSON.stringify(ct.fields));
                        }
                    });
                }
            },
        },
    },
    methods: {
        handleContentTypeClick(index) {
            if (this.selectedContentTypeIndex === index) {
                return;
            }

            if (this.selectedContentType && this.hasUnsavedChanges(this.selectedContentType.originalContentTypeID)) {
                const previousIndex = this.selectedContentTypeIndex;

                this.$nextTick(() => {
                    this.selectedContentTypeIndex = previousIndex;
                });

                this.$confirm(this.$t('UnsavedChanges'), {
                    title: this.$t('Warning'),
                    icon: 'mdi-alert',
                    color: 'warning',
                    buttonTrueText: this.$t('Save'),
                    buttonFalseText: this.$t('DiscardChanges'),
                }).then((res) => {
                    if (res == 1) {
                        this.saveCurrentContentType().then(() => {
                            this.switchToContentType(index);
                        });
                    } else if (res == 2) {
                        this.discardChanges(this.selectedContentType.originalContentTypeID);
                        this.switchToContentType(index);
                    }
                });
            } else {
                this.switchToContentType(index);
            }
        },

        switchToContentType(index) {
            this.selectedContentTypeIndex = index;
            const ct = this.contentTypes[index];

            if (!ct || !ct.fields) {
                this.currentFields = [];
                return;
            }

            const existingConfig = this.existingConfigs.find(
                config => config.allVersionsIds.includes(ct.id)
            );

            if (existingConfig && existingConfig.fieldCodesWithOrder) {
                const fieldCodesWithOrder = existingConfig.fieldCodesWithOrder;
                const fields = JSON.parse(JSON.stringify(ct.fields));
                const configuredFields = [];
                const unconfiguredFields = [];

                fields.forEach(field => {
                    if (Object.hasOwn(fieldCodesWithOrder, field.code)) {
                        configuredFields.push({
                            field,
                            order: fieldCodesWithOrder[field.code]
                        });
                    } else {
                        unconfiguredFields.push(field);
                    }
                });

                configuredFields.sort((a, b) => a.order - b.order);

                this.currentFields = [
                    ...configuredFields.map(item => item.field),
                    ...unconfiguredFields
                ];

                this.originalFieldsOrder[ct.id] = JSON.parse(JSON.stringify(this.currentFields));
            } else {
                this.currentFields = JSON.parse(JSON.stringify(ct.fields));
                this.originalFieldsOrder[ct.id] = JSON.parse(JSON.stringify(ct.fields));
            }
        },

        onFieldOrderChange() {
            if (this.selectedContentType) {
                this.$set(this.unsavedChanges, this.selectedContentType.originalContentTypeID, true);
            }
        },

        hasUnsavedChanges(contentTypeId) {
            return this.unsavedChanges[contentTypeId] === true;
        },

        discardChanges(contentTypeId) {
            this.$set(this.unsavedChanges, contentTypeId, false);
            if (this.originalFieldsOrder[contentTypeId]) {
                const ct = this.contentTypes.find(c => c.id === contentTypeId);
                if (ct) {
                    ct.fields = JSON.parse(JSON.stringify(this.originalFieldsOrder[contentTypeId]));
                }
            }
        },
        async saveCurrentContentType() {
            if (!this.selectedContentType) return;
            this.loading = true;
            const originalContentTypeId = this.selectedContentType.originalContentTypeID;

            const fieldOrderDict = {};
            this.currentFields.forEach((field, index) => {
                fieldOrderDict[field.code] = index + 1;
            });

            try {
                const existingConfigIndex = this.existingConfigs.findIndex(
                    config => config.contentTypeId === originalContentTypeId
                );

                if (existingConfigIndex === -1) return;

                const hasFieldOrderConfig =
                    this.existingConfigs[existingConfigIndex].fieldCodesWithOrder &&
                    Object.keys(this.existingConfigs[existingConfigIndex].fieldCodesWithOrder).length > 0;

                if (hasFieldOrderConfig) {
                    await UserContentTypeResultsOrdersService.update(
                        { FieldCodesWithOrder: fieldOrderDict },
                        originalContentTypeId
                    );
                } else {
                    await UserContentTypeResultsOrdersService.save({
                        contentTypeId: originalContentTypeId,
                        FieldCodesWithOrder: fieldOrderDict,
                    });
                }

                this.$set(this.existingConfigs, existingConfigIndex, {
                    ...this.existingConfigs[existingConfigIndex],
                    fieldCodesWithOrder: fieldOrderDict,
                });

                this.originalFieldsOrder[originalContentTypeId] = JSON.parse(JSON.stringify(this.currentFields));
                this.selectedContentType.fields = JSON.parse(JSON.stringify(this.currentFields));
                this.$set(this.unsavedChanges, originalContentTypeId, false);
                Utils.toast.success(this.$t('SavedSuccessfully'));

                this.madeChanges = true;
            } catch (error) {
                console.error('Error saving fields order:', error);
            } finally {
                this.loading = false;
            }
        },
        show() {
            this.open = true;
        },
        async hide() {
            if (this.madeChanges) {
                await this.$store.dispatch('account/getUserContentTypeResultsOrders');
                location.reload();
            } else {
                this.open = false;
            }
        },
    },
};
</script>

<style scoped>
.flip-list-move {
  transition: transform 0.5s;
}

.drag-handle {
  cursor: move;
}

.v-card {
  transition: all 0.3s;
}
</style>